// @ts-strict

import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
// import { ChartDataSets } from 'chart.js';
import { chunk, toNumber } from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { GraphFilterOption, StatisticsItem } from '@pages/statistics/shared/interfaces/statistics';
import { CountriesStatsRequestParams } from '@services/statistics/statistics.types';

@Injectable({
  providedIn: 'root'
})
export class StatisticsGraphsService {
  constructor(private translate: TranslateService) {}

  public emptyStatisticsItem: StatisticsItem = {
    acceptance: '0.00',
    conversion: '0.00',
    currency: 'EUR',
    earnings_together: '0.00',
    earnings_status_payment_waiting: '0.00',
    earnings_status_rejected: '0.00',
    earnings_status_to_payment: '0.00',
    earnings_status_wait: '0.00',
    epc: '0.00',
    leads_together: 0,
    leads: 0,
    leads_status_payment_waiting: 0,
    leads_status_rejected: 0,
    leads_status_wait: 0,
    visits: 0
  };
  public statisticsSelectedFilters: GraphFilterOption[] = [];
  public requestParams: CountriesStatsRequestParams = {
    date_from: new Date().toString(),
    date_to: new Date().toString()
  };
  public requestParams$ = new BehaviorSubject<CountriesStatsRequestParams>(this.requestParams);
  public graphData$ = new BehaviorSubject<StatisticsItem[]>([]);
  public graphCompareData$ = new BehaviorSubject<StatisticsItem[]>([]);
  public _loadingGraphDataSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this._loadingGraphDataSubject.asObservable();
  private colorMap = {
    earnings_together: { filColor: '#45BF52', borderColor: '#E9FFE6' },
    visits: { filColor: '#0075FF', borderColor: '#EBF4FF' },
    conversion: { filColor: '#9C27B0', borderColor: '#FCEBFF' },
    leads_together: { filColor: '#FF8C03', borderColor: '#a6ff9a' },
    earnings_status_to_payment: { filColor: '#45BF52', borderColor: '#E9FFE6' },
    earnings_status_payment_waiting: { filColor: '#a6ff9a', borderColor: '#E9FFE6' },
    earnings_status_wait: { filColor: '#FFC003', borderColor: '#ffedb5' },
    earnings_status_rejected: { filColor: '#660B01', borderColor: '#FFE8E6' },
    leads: { filColor: '#FF8C03', borderColor: '#FFF4E6' },
    leads_status_payment_waiting: { filColor: '#FF8C03', borderColor: '#FFF4E6' },
    leads_status_rejected: { filColor: '#FF8C03', borderColor: '#FFF4E6' },
    leads_status_wait: { filColor: '#664D01', borderColor: '#FFF4E6' },
    acceptance: { filColor: '#005F66', borderColor: '#FCEBFF' },
    epc: { filColor: '#00FFEC', borderColor: '#EBFFFE' }
  };

  public resetGraphData = () => {
    this.graphCompareData$.next([]);
    this.graphData$.next([]);
  };

  public createDataTable = (startDate: string | Date, endDate: string | Date, data: StatisticsItem[]): StatisticsItem[] => {
    const allDays = this.getDatesBetweenDates(startDate, endDate);

    // Unikaj nadmiernego mapowania
    const processedData = data.map(item => ({
      ...item,
      earnings_together: this.safeSum(item.earnings_status_payment_waiting, item.earnings_status_to_payment, item.earnings_status_wait),
      leads_together: Number(this.safeSum(item.leads, item.leads_status_payment_waiting, item.leads_status_wait))
    }));

    return allDays.map(day => {
      const match = processedData.find(item => moment(item.day).isSame(day));
      return match || { ...this.emptyStatisticsItem, day, earnings_together: '0.00', leads_together: 0 };
    });
  };

  // Bezpieczne sumowanie
  private safeSum(...values: (string | number | undefined)[]): string {
    const sum = values
      .filter(val => val !== undefined)
      .map(val => Number(val || 0))
      .reduce((a, b) => a + b, 0);

    return sum.toFixed(2);
  }

  public getPointColors(dataLabel: string): { filColor: string; borderColor: string } {
    return this.colorMap[dataLabel] || { filColor: '#000', borderColor: '#fff' };
  }

  public getYAxisID(name: string): string {
    let axisName: string;
    if (name.includes('earn')) {
      axisName = 'yAxisEarns';
    } else {
      axisName = name;
    }
    return axisName;
  }

  public getBackgroundGradientColor(dataLabel: string): CanvasGradient | undefined {
    const context = document.getElementById('statisticsLineChart');

    let primaryWhiteGradient: CanvasGradient;
    let primaryGreenGradient: CanvasGradient;
    let pastelPrimaryWhiteGradient: CanvasGradient;
    let warningWhiteGradient: CanvasGradient;
    let dangerWhiteGradient: CanvasGradient;
    let indigoWhiteGradient: CanvasGradient;
    let amberWhiteGradient: CanvasGradient;
    let purpleWhiteGradient: CanvasGradient;
    let turquoiseWhiteGradient: CanvasGradient;
    let pastelPrimaryPrimary: CanvasGradient;

    if (context) {
      const ctx = (document.getElementById('statisticsLineChart') as HTMLCanvasElement).getContext('2d');

      if (ctx !== null && document.getElementById('graphCanvasContainer') !== null) {
        ctx.canvas.width = (document.getElementById('graphCanvasContainer') as HTMLCanvasElement).clientWidth;

        primaryWhiteGradient = ctx.createLinearGradient(0, 0, 0, 500);
        primaryWhiteGradient.addColorStop(0, 'rgba(69, 191, 82, 0.5)');
        primaryWhiteGradient.addColorStop(0.5, 'rgba(69, 191, 82, 0.4)');

        primaryGreenGradient = ctx.createLinearGradient(0, 0, 0, 500);
        primaryGreenGradient.addColorStop(0, 'rgba(69, 191, 82, 1)');
        primaryGreenGradient.addColorStop(0.5, 'rgba(69, 191, 82, 0.5)');

        pastelPrimaryWhiteGradient = ctx.createLinearGradient(0, 0, 0, 500);
        pastelPrimaryWhiteGradient.addColorStop(0, 'rgba(157, 249, 141, 1)');
        pastelPrimaryWhiteGradient.addColorStop(0.5, 'rgba(157, 249, 141, 0.5)');

        warningWhiteGradient = ctx.createLinearGradient(0, 0, 0, 600);
        warningWhiteGradient.addColorStop(0, 'rgba(255, 192, 3, 1)');
        warningWhiteGradient.addColorStop(0.5, 'rgba(255, 192, 3, 0.5)');

        dangerWhiteGradient = ctx.createLinearGradient(0, 0, 0, 500);
        dangerWhiteGradient.addColorStop(0, 'rgba(102, 11, 1, 0.7)');
        dangerWhiteGradient.addColorStop(0.5, 'rgba(102, 11, 1, 0.5)');

        indigoWhiteGradient = ctx.createLinearGradient(0, 0, 0, 600);
        indigoWhiteGradient.addColorStop(0, 'rgba(0, 117, 255, 0.2)');
        indigoWhiteGradient.addColorStop(0.5, 'rgba(0, 117, 255, 0.0001)');

        amberWhiteGradient = ctx.createLinearGradient(0, 0, 0, 600);
        amberWhiteGradient.addColorStop(0, 'rgba(255, 140, 3, 0.2)');
        amberWhiteGradient.addColorStop(0.5, 'rgba(255, 140, 3, 0.0001)');

        purpleWhiteGradient = ctx.createLinearGradient(0, 0, 0, 500);
        purpleWhiteGradient.addColorStop(0, 'rgba(156, 39, 176, 0.2)');
        purpleWhiteGradient.addColorStop(0.5, 'rgba(156, 39, 176, 0.0001)');

        turquoiseWhiteGradient = ctx.createLinearGradient(0, 0, 0, 600);
        turquoiseWhiteGradient.addColorStop(0, 'rgba(0, 255, 236, 0.2)');
        turquoiseWhiteGradient.addColorStop(0.5, 'rgba(0, 255, 236, 0.0001)');

        pastelPrimaryPrimary = ctx.createLinearGradient(0, 0, 0, 600);
        pastelPrimaryPrimary.addColorStop(0, 'rgba(69, 191, 82, 0.2)');
        pastelPrimaryPrimary.addColorStop(0.5, 'rgba(69, 191, 82, 0.0001)');

        switch (dataLabel) {
          case 'earnings_together':
            return primaryGreenGradient;
          case 'earnings_status_to_payment':
            return primaryWhiteGradient;
          case 'earnings_status_payment_waiting':
            return pastelPrimaryWhiteGradient;
          case 'earnings_status_wait':
            return warningWhiteGradient;
          case 'earnings_status_rejected':
            return dangerWhiteGradient;
          case 'visits':
            return indigoWhiteGradient;
          case 'leads_together':
            return amberWhiteGradient;
          case 'leads':
            return amberWhiteGradient;
          case 'leads_status_payment_waiting':
            return amberWhiteGradient;
          case 'leads_status_rejected':
            return dangerWhiteGradient;
          case 'leads_status_wait':
            return warningWhiteGradient;
          case 'conversion':
            return purpleWhiteGradient;
          case 'epc':
            return turquoiseWhiteGradient;
          case 'acceptance':
            return pastelPrimaryPrimary;
          default:
            return undefined;
        }
      }
    }
  }

  public prepareGraphData(
    selectedData: GraphFilterOption[],
    graphData: StatisticsItem[]
  ): { preparedGraphData: any[] & { currentSeries?: string }; graphLabels: string[] } {
    selectedData.sort((a, b) => {
      const aHasEarnings = a.name?.includes('earnings');
      const bHasEarnings = b.name?.includes('earnings');
      if (aHasEarnings && !bHasEarnings) return 1;
      if (!aHasEarnings && bHasEarnings) return -1;
      return 0;
    }); // if earnings objects will not be at the end of array, we will have problem with indexing other axes

    const preparedGraphData = selectedData.map(({ name, translation, compare }, index) => {
      const tempArray: any[] = [];
      const tempCompareArray: any[] = [];
      if (name) {
        const { borderColor, filColor } = this.getPointColors(name);
        const currentSeries = name as keyof StatisticsItem;
        const currentSeriesTranslation = this.translate.instant(`view_translations.${translation}`).toLowerCase();

        graphData.forEach(item => {
          if (item[currentSeries] !== false && !item.compare) {
            tempArray.push(item[currentSeries]);
          }
          if (item.compare) {
            tempCompareArray.push(item[currentSeries]);
          }
        });

        return {
          currentSeries: currentSeries,
          data: compare ? tempCompareArray : tempArray,
          label: currentSeriesTranslation,
          lineTension: 0,
          borderColor: `${filColor}`,
          borderDash: compare ? [5, 5] : [],
          backgroundColor: this.getBackgroundGradientColor(currentSeries),
          borderWidth: 1,
          pointBackgroundColor: `${filColor}`,
          pointBorderColor: `${borderColor}`,
          pointBorderWidth: 0,
          pointRadius: 0,
          pointHoverBackgroundColor: `${filColor}`,
          pointHoverBorderColor: `${borderColor}`,
          pointHoverBorderWidth: 0,
          pointHoverRadius: 3,
          fill: 'origin',
          yAxisID: name.includes('earn') ? 'yAxisEarns' : `yAxis${index + 1}`
        };
      }
    });
    const graphLabels: string[] = [];
    const legendValues: any[] = [];

    graphData.forEach(item => {
      if (item.day) {
        const dateTemp = item.day.split('-');
        graphLabels.push(`${dateTemp[2]}.${dateTemp[1]}.${dateTemp[0]}`);
        legendValues.push('');
      }
      if (item.dayFrom && item.dayTo) {
        const dateTemp1 = item.dayFrom.split('-');
        const dateTemp2 = item.dayTo.split('-');

        graphLabels.push(`${dateTemp1[2]}.${dateTemp1[1]}.${dateTemp1[0]}-${dateTemp2[2]}.${dateTemp2[1]}.${dateTemp2[0]}`);
        legendValues.push('');
      }
    });

    const latestOptionWithNoData = preparedGraphData.reverse().find(option => option?.data.every(dataItem => dataItem === 0 || dataItem === '0.00'));

    let { borderColor, filColor } = latestOptionWithNoData
      ? this.getPointColors(latestOptionWithNoData.currentSeries)
      : { borderColor: '#F4F6F9', filColor: '#9d9d9d' };

    return {
      preparedGraphData: [
        // {
        //   data: legendValues,
        //   label: ''
        //   // lineTension: 0,
        //   // borderColor: 'rgba( 255, 255, 255, 0.1 )',
        //   // borderWidth: 0,
        //   // pointBackgroundColor: filColor,
        //   // pointBorderColor: borderColor,
        //   // pointBorderWidth: 0,
        //   // pointRadius: 0,
        //   // pointHoverBackgroundColor: filColor,
        //   // pointHoverBorderColor: borderColor,
        //   // pointHoverBorderWidth: 0,
        //   // pointHoverRadius: 3,
        //   // yAxisID: 0 as unknown as string
        // },
        ...(preparedGraphData as Record<string, any>[])
      ],
      graphLabels: [...new Set(graphLabels)]
    };
  }

  public dashboardWidgetGraphFilterOptions: GraphFilterOption[] = [
    {
      name: 'visits',
      translation: 'visits',
      status: false,
      fillColor: '#0075FF',
      borderColor: '#E9FFE6'
    },
    {
      name: 'conversion',
      translation: 'conversion',
      status: false,
      fillColor: '#9C27B0',
      borderColor: '#FCEBFF'
    },
    {
      name: 'leads_together',
      translation: 'leads',
      status: false,
      fillColor: '#FF8C03',
      borderColor: '#a6ff9a'
    },
    {
      name: 'earnings_together',
      translation: 'earnings',
      status: false,
      // fillColor: '#01660b',
      fillColor: '#45bf52',
      borderColor: '#E9FFE6'
    },
    {
      name: 'earnings_status_to_payment',
      translation: 'withdrawal_commissions',
      status: false,
      fillColor: '#45BF52',
      borderColor: '#E9FFE6'
    },
    {
      name: 'earnings_status_payment_waiting',
      translation: 'accepted_commissions',
      status: false,
      fillColor: '#a6ff9a',
      borderColor: '#E9FFE6'
    },
    {
      name: 'earnings_status_wait',
      translation: 'pending_commissions',
      status: false,
      fillColor: '#FFC003',
      borderColor: '#ffedb5'
    },
    {
      name: 'earnings_status_rejected',
      translation: 'rejected_commissions',
      status: false,
      fillColor: '#660B01',
      borderColor: '#FFE8E6'
    },
    {
      name: 'leads',
      translation: 'leads_to_pay',
      status: false,
      fillColor: '#FF8C03',
      borderColor: '#FFF4E6'
    },
    {
      name: 'leads_status_payment_waiting',
      translation: 'leads_status_payment_waiting',
      status: false,
      fillColor: '#FF8C03',
      borderColor: '#FFF4E6'
    },
    {
      name: 'leads_status_rejected',
      translation: 'leads_status_rejected',
      status: false,
      fillColor: '#FF8C03',
      borderColor: '#FFF4E6'
    },
    {
      name: 'leads_status_wait',
      translation: 'leads_status_wait',
      status: false,
      fillColor: '#664D01',
      borderColor: '#FFF4E6'
    },
    {
      name: 'acceptance',
      translation: 'acceptance_factor',
      status: false,
      fillColor: '#005F66',
      borderColor: '#FCEBFF'
    },
    {
      name: 'epc',
      translation: 'epc',
      status: false,
      fillColor: '#00FFEC',
      borderColor: '#EBFFFE'
    }
  ];

  public dashboardWidgetInitialGraphFilterOptions: GraphFilterOption[] = [
    {
      name: 'visits',
      translation: 'visits',
      status: true,
      fillColor: '#0075FF',
      borderColor: '#E9FFE6'
    },
    {
      name: 'conversion',
      translation: 'conversion',
      status: true,
      fillColor: '#9C27B0',
      borderColor: '#FCEBFF'
    },
    {
      name: 'leads_together',
      translation: 'leads',
      status: true,
      fillColor: '#FF8C03',
      borderColor: '#a6ff9a'
    },
    {
      name: 'earnings_together',
      translation: 'earnings',
      status: true,
      fillColor: '#45BF52',
      borderColor: '#E9FFE6'
    }
  ];

  private groupGraphDataByWeek = (graphData: StatisticsItem[]): StatisticsItem[] => {
    const chunkedData = chunk(graphData, 7);

    const sumTotal = (previousValue: string | number, currentValue: string | number): string | number | undefined => {
      if (typeof previousValue === 'string' && typeof currentValue === 'string') {
        return (parseFloat(previousValue) + parseFloat(currentValue)).toFixed(2).toString();
      }

      if (typeof previousValue === 'number' && typeof currentValue === 'number') {
        return previousValue + currentValue;
      }
    };

    const grouped = chunkedData.map((_items, index) => {
      return chunkedData[index].reduce((previousValue, currentValue) => ({
        dayFrom: chunkedData[index][0].day,
        dayTo: chunkedData[index][chunkedData[index].length - 1].day,
        compare: currentValue.compare,
        currency: currentValue.currency,
        acceptance: sumTotal(previousValue.acceptance as string, currentValue.acceptance as string) as string,
        conversion: sumTotal(previousValue.conversion as string, currentValue.conversion as string) as string,
        earnings_status_payment_waiting: sumTotal(
          previousValue.earnings_status_payment_waiting as string,
          currentValue.earnings_status_payment_waiting as string
        ) as string,
        earnings_together: sumTotal(previousValue.earnings_together as string, currentValue.earnings_together as string) as string,
        earnings_status_rejected: sumTotal(previousValue.earnings_status_rejected as string, currentValue.earnings_status_rejected as string) as string,
        earnings_status_to_payment: sumTotal(previousValue.earnings_status_to_payment as string, currentValue.earnings_status_to_payment as string) as string,
        earnings_status_wait: sumTotal(previousValue.earnings_status_wait as string, currentValue.earnings_status_wait as string) as string,
        epc: sumTotal(previousValue.epc as string, currentValue.epc as string) as string,
        visits: sumTotal(previousValue.visits as number, currentValue.visits as number) as number,
        leads_together: sumTotal(previousValue.leads_together as number, currentValue.leads_together as number) as number,
        leads: sumTotal(previousValue.leads as number, currentValue.leads as number) as number,
        leads_status_payment_waiting: sumTotal(
          previousValue.leads_status_payment_waiting as number,
          currentValue.leads_status_payment_waiting as number
        ) as number,
        leads_status_rejected: sumTotal(previousValue.leads_status_rejected as number, currentValue.leads_status_rejected as number) as number,
        leads_status_wait: sumTotal(previousValue.leads_status_wait as number, currentValue.leads_status_wait as number) as number
      }));
    });

    return grouped;
  };

  private getDatesBetweenDates = (startDate: string | Date, endDate: string | Date): string[] => {
    const dates: string[] = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
      dates.push(formatDate(currentDate, 'yyyy-MM-dd', 'en'));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };
}
